import { lazy, Suspense } from "react";
import { QueryClientProvider } from "@tanstack/react-query";
import {
  BrowserRouter,
  Navigate,
  Outlet,
  Route,
  Routes,
} from "react-router-dom";
import { queryClient } from "@/lib/query-client";
import { AppShell } from "@/components/AppShell";
import { LoadingRegion } from "@/components/ui/loading";
import { UpdateToast } from "@/components/UpdateToast";
import { RequireSession } from "@/lib/auth/RequireSession";
import {
  RequireCharactersWorkspace,
  WorkspaceLayout,
} from "@/lib/workspace/ActiveWorkspace";
import { WorkspaceEntryScreen } from "@/screens/workspace/WorkspaceEntryScreen";
import { CharactersScreen } from "@/screens/CharactersScreen";

// The first content screen stays in the entry bundle. `/` resolves the
// viewer's organization and redirects straight here, so splitting it would buy
// nothing but a second round trip on the one path almost every visit takes.
// Everything a user may never reach is split out.
const NotFoundScreen = lazy(() =>
  import("@/screens/NotFoundScreen").then(({ NotFoundScreen }) => ({
    default: NotFoundScreen,
  })),
);

// Both auth screens are off the first-paint path: a returning user with a live
// cookie never renders either one, and a refused visitor sees /no-access once
// in their life.
const LoginScreen = lazy(() =>
  import("@/screens/LoginScreen").then(({ LoginScreen }) => ({
    default: LoginScreen,
  })),
);

const NoAccessScreen = lazy(() =>
  import("@/screens/NoAccessScreen").then(({ NoAccessScreen }) => ({
    default: NoAccessScreen,
  })),
);

const OrgScreen = lazy(() =>
  import("@/screens/OrgScreen").then(({ OrgScreen }) => ({
    default: OrgScreen,
  })),
);
const WorkspaceDirectoryScreen = lazy(() =>
  import("@/screens/workspace/WorkspaceDirectoryScreen").then(
    ({ WorkspaceDirectoryScreen }) => ({ default: WorkspaceDirectoryScreen }),
  ),
);
const NewCharacterScreen = lazy(() =>
  import("@/screens/CharacterWorkbenchScreen").then(
    ({ NewCharacterScreen }) => ({ default: NewCharacterScreen }),
  ),
);
const CharacterArchiveScreen = lazy(() =>
  import("@/screens/CharacterArchiveScreen").then(
    ({ CharacterArchiveScreen }) => ({ default: CharacterArchiveScreen }),
  ),
);
const CharacterWorkbenchScreen = lazy(() =>
  import("@/screens/CharacterWorkbenchScreen").then(
    ({ CharacterWorkbenchScreen }) => ({ default: CharacterWorkbenchScreen }),
  ),
);
const CharacterDetailScreen = lazy(() =>
  import("@/screens/CharacterDetailScreen").then(
    ({ CharacterDetailScreen }) => ({ default: CharacterDetailScreen }),
  ),
);
const DraftsScreen = lazy(() =>
  import("@/screens/drafts/DraftsScreen").then(({ DraftsScreen }) => ({
    default: DraftsScreen,
  })),
);
const DraftReviewScreen = lazy(() =>
  import("@/screens/drafts/DraftReviewScreen").then(
    ({ DraftReviewScreen }) => ({ default: DraftReviewScreen }),
  ),
);
const JobScreen = lazy(() =>
  import("@/screens/CharacterDetailScreen").then(({ JobScreen }) => ({
    default: JobScreen,
  })),
);
const AuditScreen = lazy(() =>
  import("@/screens/AuditScreen").then(({ AuditScreen }) => ({
    default: AuditScreen,
  })),
);

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AppShell>
          <Suspense fallback={<LoadingRegion label="Loading…" />}>
            <Routes>
              {/* Public, and they must stay public: /login is where the
                    guard sends an expired session, and /no-access is reached
                    by a 302 from the callback with no session at all.
                    Guarding either one is an infinite redirect. */}
              <Route path="/login" element={<LoginScreen />} />
              <Route path="/no-access" element={<NoAccessScreen />} />

              {/* One guard for the whole authenticated tree, as a pathless
                    layout route. Repeating it per route is how a new route
                    ends up unguarded — the mistake is invisible in review. */}
              <Route
                element={
                  <RequireSession>
                    <Outlet />
                  </RequireSession>
                }
              >
                <Route path="/" element={<WorkspaceEntryScreen />} />
                <Route
                  path="/organizations"
                  element={<WorkspaceDirectoryScreen />}
                />

                {/* Every content route is scoped by a real organization
                      membership from /v1/me. The slug remains in the URL so
                      deep links and restored tabs carry the organization. */}
                <Route path="/w/:workspaceSlug" element={<WorkspaceLayout />}>
                  {/* Content lives in the organization's own Characters
                        workspace. An organization without one keeps Team
                        settings; every route that reads content (audit
                        included) waits behind this guard instead of firing
                        requests that can only answer not-provisioned. */}
                  <Route element={<RequireCharactersWorkspace />}>
                    <Route
                      index
                      element={<Navigate to="characters" replace />}
                    />
                    <Route path="characters" element={<CharactersScreen />} />
                    <Route
                      path="characters/archive"
                      element={<CharacterArchiveScreen />}
                    />
                    <Route
                      path="characters/:characterID/*"
                      element={<CharacterDetailScreen />}
                    />
                    <Route path="drafts" element={<DraftsScreen />} />
                    <Route path="drafts/new" element={<NewCharacterScreen />} />
                    <Route
                      path="drafts/:characterID"
                      element={<CharacterWorkbenchScreen />}
                    />
                    <Route
                      path="drafts/:characterID/review"
                      element={<DraftReviewScreen />}
                    />
                    <Route path="jobs/:jobID" element={<JobScreen />} />
                    <Route path="settings/audit" element={<AuditScreen />} />
                  </Route>
                  <Route path="settings/team" element={<OrgScreen />} />
                  {/* Both settings screens used to sit at the workspace root,
                        and those addresses are already in bookmarks and pasted
                        links. They keep resolving instead of landing on 404. */}
                  <Route
                    path="team"
                    element={<Navigate to="../settings/team" replace />}
                  />
                  <Route
                    path="audit"
                    element={<Navigate to="../settings/audit" replace />}
                  />
                </Route>

                {/* No redirect to /404: rewriting the URL destroys the
                      address that actually failed, which is the one piece of
                      evidence a bad-link report needs. */}
                <Route path="*" element={<NotFoundScreen />} />
              </Route>
            </Routes>
          </Suspense>
        </AppShell>
        <UpdateToast />
      </BrowserRouter>
    </QueryClientProvider>
  );
}
